import { Component, OnChanges, SimpleChanges, inject, input } from '@angular/core';
import { DetailFieldForFullAssetPage } from '../../../shared/interfaces/research-portal/full-asset-page/asset-model/general-helpers-model/detail-field-for-full-asset-page.interface';
import { MediaService } from '@exl-ng/mulo-core';
import { AssetPageService } from '../../asset-page.service';
import { TranslateModule } from '@ngx-translate/core';
import { ExpandableComponent, DictionaryComponent, DictionaryItemComponent, DictionaryItemContentDirective } from '@exl-ng/mulo-common';

interface DetailFieldParsed {
    code: string;
    value: string;
    lines: DetailFieldParsedLine[];
    hasMultipleAffiliations: boolean;
}

interface DetailFieldParsedLine {
    text: string;
    affiliations?: string[];
    url?: string;
}

const AffiliationSeparator = '$$A';
const LineSeparator = '$$E';
const UrlLabelOpen = '$$T';
const UrlLabelClose = '$$L';

@Component({
    selector: 'esp-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    imports: [
        ExpandableComponent,
        DictionaryComponent,
        DictionaryItemComponent,
        DictionaryItemContentDirective,
        TranslateModule,
    ]
})
export class DetailsComponent implements OnChanges {
    media = inject(MediaService);
    private assetPageService = inject(AssetPageService);

    readonly details = input<DetailFieldForFullAssetPage[]>(undefined);
    parsedDetails: DetailFieldParsed[];
    detailsExpanded = false;

    ngOnChanges(changes: SimpleChanges): void {
        const newDetails: DetailFieldForFullAssetPage[] =
            changes['details']?.currentValue;
        if (newDetails) {
            // Rather than parse it dynamically from the HTML (run on every update),
            // parse on input change and store locally
            this.parsedDetails = newDetails.map((field) => ({
                ...field,
                hasMultipleAffiliations:
                    field.value.includes(AffiliationSeparator),
                lines: field.value.split(LineSeparator).map((line) => {
                    if (
                        line.includes(UrlLabelOpen) &&
                        line.includes(UrlLabelClose)
                    ) {
                        const parts = line.split(UrlLabelClose);
                        return {
                            text: parts[0].replace(UrlLabelOpen, ''),
                            url: parts[1],
                        };
                    } else if (line.includes(AffiliationSeparator)) {
                        const parts = line.split(AffiliationSeparator);
                        return {
                            text: parts[0],
                            affiliations: parts.slice(1),
                        };
                    } else {
                        return { text: line };
                    }
                }),
            }));
        }
    }

    getExpandTranslation(code: string, expand = true) {
        const translatedCode = this.assetPageService.getTranslation(
            'esploro.research.conf.research.portal.label.prefix.' + code,
        );

        let response: string;
        if (expand) {
            response = this.assetPageService.getTranslation(
                'research.portal.fulldisplay.details.showSection',
                { val0: translatedCode },
            );
        } else {
            response = this.assetPageService.getTranslation(
                'research.portal.fulldisplay.details.collapseSection',
                { val0: translatedCode },
            );
        }

        return response;
    }

    handleDetailsExpandChange(expanded: boolean) {
        this.detailsExpanded = expanded;
    }
}
